/**
 * Departments & Locations — the tenant's organizational grouping
 * (RFC procedure-instance, Phase 1). A shallow Department → Location
 * structure that procedure instances (and, later, sessions) are tagged
 * with for analytics. Tenant_admin surface; mirrors the Procedures page.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SchoolIcon from '@mui/icons-material/School';
import ScienceIcon from '@mui/icons-material/Science';
import ClassIcon from '@mui/icons-material/Class';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';

import { activeAdminTenantId, canWriteTenant, useAuth } from '../../auth/AuthContext';
import * as orgApi from '../../api/organizations';
import { LOCATION_KINDS, LOCATION_KIND_LABELS } from '../../api/organizations';
import { SearchableSelect, sortOptions } from '../../components/SearchableSelect';
import * as devicesApi from '../../api/devices';
import * as analyticsApi from '../../api/analytics';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';

// Kinds alphabetically, with the catch-all "Other" kept last.
const KIND_OPTIONS = [
  ...sortOptions(
    LOCATION_KINDS.filter((k) => k !== 'other').map((k) => ({
      value: k as string,
      label: LOCATION_KIND_LABELS[k],
    })),
  ),
  { value: 'other', label: LOCATION_KIND_LABELS.other },
];
const NONE_OPTION = [{ value: '', label: '— none —' }];

/** A facility's kind → a small icon, so the type reads at a glance. */
const KIND_ICON: Record<string, ReactNode> = {
  or: <LocalHospitalIcon fontSize="small" />,
  sim_lab: <SchoolIcon fontSize="small" />,
  cadaver_lab: <ScienceIcon fontSize="small" />,
  classroom: <ClassIcon fontSize="small" />,
  ultrasound: <MonitorHeartIcon fontSize="small" />,
  other: <MeetingRoomIcon fontSize="small" />,
};

/** A small "N label" count chip for a department header. */
function CountChip({ value, label }: { value: number; label: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${value} ${label}`}
      sx={{ fontWeight: 700 }}
    />
  );
}

/** A department card: name + at-a-glance counts + actions, with its facilities
 *  nested inside (children). Models the hospital's department → rooms shape. */
function DeptCard({
  name,
  subtitle,
  facilityCount,
  sessions,
  participants,
  devices,
  canWrite,
  onAddFacility,
  onEdit,
  onDelete,
  children,
}: {
  name: string;
  subtitle?: string;
  facilityCount: number;
  sessions?: number;
  participants?: number;
  devices: number;
  canWrite: boolean;
  onAddFacility?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  children: ReactNode;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1.25 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {name}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}
            >
              <CountChip
                value={facilityCount}
                label={facilityCount === 1 ? 'facility' : 'facilities'}
              />
              {sessions != null && (
                <CountChip value={sessions} label="sessions" />
              )}
              {participants != null && (
                <CountChip
                  value={participants}
                  label={participants === 1 ? 'participant' : 'participants'}
                />
              )}
              {devices > 0 && (
                <CountChip
                  value={devices}
                  label={devices === 1 ? 'device' : 'devices'}
                />
              )}
            </Stack>
          </Box>
          {canWrite && (
            <Stack direction="row" spacing={0.5}>
              {onAddFacility && (
                <Tooltip title="Add facility">
                  <IconButton
                    size="small"
                    aria-label={`Add facility to ${name}`}
                    onClick={onAddFacility}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onEdit && (
                <Tooltip title="Edit department">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${name}`}
                    onClick={onEdit}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete department">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${name}`}
                    onClick={onDelete}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
        <Box sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

/** One facility (room) row nested under a department: kind icon + name +
 *  kind label + device count + edit/delete. */
function FacilityRow({
  loc,
  deviceCount,
  canWrite,
  onEdit,
  onDelete,
}: {
  loc: orgApi.Location;
  deviceCount: number;
  canWrite: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const kindLabel =
    LOCATION_KIND_LABELS[loc.kind as orgApi.LocationKind] ?? loc.kind;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        py: 0.9,
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>
        {KIND_ICON[loc.kind] ?? KIND_ICON.other}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {loc.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {kindLabel}
        </Typography>
      </Box>
      {deviceCount > 0 && (
        <CountChip
          value={deviceCount}
          label={deviceCount === 1 ? 'device' : 'devices'}
        />
      )}
      {canWrite && (
        <>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              aria-label={`Edit ${loc.name}`}
              onClick={onEdit}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              aria-label={`Delete ${loc.name}`}
              onClick={onDelete}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  );
}

export function TenantOrganization() {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);

  const [departments, setDepartments] = useState<orgApi.Department[] | null>(
    null,
  );
  const [locations, setLocations] = useState<orgApi.Location[] | null>(null);
  const [devices, setDevices] = useState<devicesApi.Device[]>([]);
  const [deptRollup, setDeptRollup] =
    useState<analyticsApi.DeptRollup | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    // Departments + facilities are the core content; devices + analytics are
    // optional header-count enrichments, so a failure in either just hides
    // those numbers rather than breaking the page.
    const [dRes, lRes, devRes, anRes] = await Promise.allSettled([
      orgApi.listDepartments(tenantId),
      orgApi.listLocations(tenantId),
      devicesApi.listDevices(tenantId),
      analyticsApi.getByDepartment(tenantId, 'duration'),
    ]);
    if (dRes.status === 'fulfilled') setDepartments(dRes.value);
    else setDepartments([]);
    if (lRes.status === 'fulfilled') setLocations(lRes.value);
    else setLocations([]);
    if (dRes.status === 'rejected' || lRes.status === 'rejected') {
      const reason =
        dRes.status === 'rejected'
          ? dRes.reason
          : (lRes as PromiseRejectedResult).reason;
      setError(extractErrorMessage(reason, 'failed to load'));
    }
    setDevices(devRes.status === 'fulfilled' ? devRes.value : []);
    setDeptRollup(anRes.status === 'fulfilled' ? anRes.value : null);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Facilities grouped under their department (null → unassigned).
  const facilitiesByDept = useMemo(() => {
    const m = new Map<string | null, orgApi.Location[]>();
    for (const l of locations ?? []) {
      const key = l.department_id ?? null;
      const arr = m.get(key);
      if (arr) arr.push(l);
      else m.set(key, [l]);
    }
    return m;
  }, [locations]);

  // Device counts by department + by facility, from each device's default org
  // context (direct department tag, or via its default facility's department).
  const { deviceByDept, deviceByLoc } = useMemo(() => {
    const byDept = new Map<string, number>();
    const byLoc = new Map<string, number>();
    const locDept = new Map((locations ?? []).map((l) => [l.id, l.department_id]));
    for (const d of devices) {
      if (d.default_location_id) {
        byLoc.set(
          d.default_location_id,
          (byLoc.get(d.default_location_id) ?? 0) + 1,
        );
      }
      const deptId =
        d.default_department_id ??
        (d.default_location_id ? locDept.get(d.default_location_id) ?? null : null);
      if (deptId) byDept.set(deptId, (byDept.get(deptId) ?? 0) + 1);
    }
    return { deviceByDept: byDept, deviceByLoc: byLoc };
  }, [devices, locations]);

  // Session + participant counts per department (analytics rollup).
  const statsByDept = useMemo(() => {
    const m = new Map<string, { sessions: number; participants: number }>();
    for (const r of deptRollup?.rows ?? []) {
      if (r.department_id) {
        m.set(r.department_id, {
          sessions: r.session_count,
          participants: r.participant_count,
        });
      }
    }
    return m;
  }, [deptRollup]);

  const q = search.trim().toLowerCase();
  const facilityMatches = (l: orgApi.Location) =>
    l.name.toLowerCase().includes(q) ||
    (LOCATION_KIND_LABELS[l.kind as orgApi.LocationKind] ?? l.kind)
      .toLowerCase()
      .includes(q);

  // Add a facility with its department pre-selected (per-department "+").
  const openLocForDept = (deptId: string) => {
    openLoc(null);
    setLocDept(deptId);
  };

  // ---- Department dialog ----
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptEditing, setDeptEditing] = useState<orgApi.Department | null>(null);
  const [deptNameInput, setDeptNameInput] = useState('');
  const [deptSubmitting, setDeptSubmitting] = useState(false);
  const [deptError, setDeptError] = useState<string | null>(null);

  const openDept = (d: orgApi.Department | null) => {
    setDeptEditing(d);
    setDeptNameInput(d?.name ?? '');
    setDeptError(null);
    setDeptOpen(true);
  };

  const submitDept = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setDeptSubmitting(true);
    setDeptError(null);
    try {
      const name = deptNameInput.trim();
      if (deptEditing) {
        await orgApi.updateDepartment(tenantId, deptEditing.id, { name });
      } else {
        await orgApi.createDepartment(tenantId, { name });
      }
      setDeptOpen(false);
      await reload();
    } catch (err: unknown) {
      setDeptError(extractErrorMessage(err, 'failed to save department'));
    } finally {
      setDeptSubmitting(false);
    }
  };

  // ---- Location dialog ----
  const [locOpen, setLocOpen] = useState(false);
  const [locEditing, setLocEditing] = useState<orgApi.Location | null>(null);
  const [locName, setLocName] = useState('');
  const [locKind, setLocKind] = useState<string>('or');
  const [locDept, setLocDept] = useState<string>('');
  const [locSubmitting, setLocSubmitting] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);

  const openLoc = (l: orgApi.Location | null) => {
    setLocEditing(l);
    setLocName(l?.name ?? '');
    setLocKind(l?.kind ?? 'or');
    setLocDept(l?.department_id ?? '');
    setLocError(null);
    setLocOpen(true);
  };

  const submitLoc = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setLocSubmitting(true);
    setLocError(null);
    try {
      const body = {
        name: locName.trim(),
        kind: locKind,
        department_id: locDept === '' ? null : locDept,
      };
      if (locEditing) {
        await orgApi.updateLocation(tenantId, locEditing.id, body);
      } else {
        await orgApi.createLocation(tenantId, body);
      }
      setLocOpen(false);
      await reload();
    } catch (err: unknown) {
      setLocError(extractErrorMessage(err, 'failed to save location'));
    } finally {
      setLocSubmitting(false);
    }
  };

  const del = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to delete'));
    }
  };

  if (!tenantId) {
    return <Alert severity="info">No tenant selected.</Alert>;
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Departments &amp; Facilities</Typography>
          <Typography variant="body2" color="text.secondary">
            Your organizational model — departments and the facilities (ORs,
            labs, classrooms) within them. Procedures, devices, and activity
            are organized and compared by these.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <RefreshButton onClick={() => void reload()} refreshing={loading} />
          {canWrite && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openDept(null)}
            >
              Add department
            </Button>
          )}
          {canWrite && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openLoc(null)}
            >
              Add facility
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {departments !== null && locations !== null && (
        <TextField
          size="small"
          fullWidth
          placeholder="Search departments and facilities…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      )}

      {departments === null || locations === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : departments.length === 0 && locations.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No departments or facilities yet. Add a department, then the
            facilities (ORs, labs, classrooms) within it.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {/* One card per department, its facilities nested beneath — the
              hospital's natural hierarchy. Counts (facilities/sessions/
              participants/devices) come from real data; a department with no
              backing analytics simply omits those chips. */}
          {departments
            .filter(
              (d) =>
                !q ||
                d.name.toLowerCase().includes(q) ||
                (facilitiesByDept.get(d.id) ?? []).some(facilityMatches),
            )
            .map((d) => {
              const facs = facilitiesByDept.get(d.id) ?? [];
              // When the search matched the department name, keep all its
              // facilities; otherwise show only the matching ones.
              const shownFacs =
                q && !d.name.toLowerCase().includes(q)
                  ? facs.filter(facilityMatches)
                  : facs;
              const stats = statsByDept.get(d.id);
              return (
                <DeptCard
                  key={d.id}
                  name={d.name}
                  facilityCount={facs.length}
                  sessions={stats?.sessions}
                  participants={stats?.participants}
                  devices={deviceByDept.get(d.id) ?? 0}
                  canWrite={canWrite}
                  onAddFacility={() => openLocForDept(d.id)}
                  onEdit={() => openDept(d)}
                  onDelete={() =>
                    del(() => orgApi.deleteDepartment(tenantId, d.id))
                  }
                >
                  {shownFacs.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 1 }}
                    >
                      No facilities in this department yet.
                    </Typography>
                  ) : (
                    shownFacs.map((l) => (
                      <FacilityRow
                        key={l.id}
                        loc={l}
                        deviceCount={deviceByLoc.get(l.id) ?? 0}
                        canWrite={canWrite}
                        onEdit={() => openLoc(l)}
                        onDelete={() =>
                          del(() => orgApi.deleteLocation(tenantId, l.id))
                        }
                      />
                    ))
                  )}
                </DeptCard>
              );
            })}

          {/* Facilities not yet assigned to a department. */}
          {(() => {
            const orphans = (facilitiesByDept.get(null) ?? []).filter(
              (l) => !q || facilityMatches(l),
            );
            if (orphans.length === 0) return null;
            return (
              <DeptCard
                name="Unassigned"
                subtitle="Facilities not yet assigned to a department"
                facilityCount={orphans.length}
                devices={0}
                canWrite={false}
              >
                {orphans.map((l) => (
                  <FacilityRow
                    key={l.id}
                    loc={l}
                    deviceCount={deviceByLoc.get(l.id) ?? 0}
                    canWrite={canWrite}
                    onEdit={() => openLoc(l)}
                    onDelete={() =>
                      del(() => orgApi.deleteLocation(tenantId, l.id))
                    }
                  />
                ))}
              </DeptCard>
            );
          })()}
        </Stack>
      )}

      {/* Department dialog */}
      <Dialog open={deptOpen} onClose={() => setDeptOpen(false)} fullWidth maxWidth="xs">
        <form onSubmit={submitDept}>
          <DialogTitle>
            {deptEditing ? 'Edit department' : 'Add department'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Name"
              fullWidth
              required
              value={deptNameInput}
              onChange={(e) => setDeptNameInput(e.target.value)}
              disabled={deptSubmitting}
              sx={{ mt: 1 }}
            />
            {deptError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deptError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeptOpen(false)} disabled={deptSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={deptSubmitting || deptNameInput.trim() === ''}
            >
              {deptSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Location dialog */}
      <Dialog open={locOpen} onClose={() => setLocOpen(false)} fullWidth maxWidth="xs">
        <form onSubmit={submitLoc}>
          <DialogTitle>
            {locEditing ? 'Edit facility' : 'Add facility'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              A place a procedure happens — an OR, sim lab, cadaver lab,
              classroom, etc.
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                autoFocus
                label="Name"
                fullWidth
                required
                value={locName}
                onChange={(e) => setLocName(e.target.value)}
                disabled={locSubmitting}
              />
              <SearchableSelect
                label="Kind"
                size="medium"
                fullWidth
                value={locKind}
                onChange={setLocKind}
                disabled={locSubmitting}
                options={KIND_OPTIONS}
                keepOrder
              />
              <SearchableSelect
                label="Department (optional)"
                size="medium"
                fullWidth
                value={locDept}
                onChange={setLocDept}
                disabled={locSubmitting}
                pinned={NONE_OPTION}
                options={(departments ?? []).map((d) => ({ value: d.id, label: d.name }))}
              />
            </Stack>
            {locError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {locError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLocOpen(false)} disabled={locSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={locSubmitting || locName.trim() === ''}
            >
              {locSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
